// PATH: src/components/catalog/CatalogTree.tsx
/**
 * Arbre du référentiel : Programme → Étape → Niveau. Maison (pas de
 * @mui/x-tree-view) : compact, compteurs discrets, recherche, nœud
 * sélectionné en surbrillance, flèches ↑ ↓ ← → au clavier.
 */
import * as React from "react";
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import type { CurriculumProgram } from "../../models/domain";
import { filterTree, plural, programCounts, sameNode, type CatalogIndex, type NodeRef, nodePath } from "./catalogModel";
import { ColorDot } from "./PlanetPreview";

interface Props {
  programs: CurriculumProgram[];
  index: CatalogIndex;
  selected: NodeRef | null;
  onSelect: (node: NodeRef) => void;
}

interface Row {
  node: NodeRef;
  depth: number;
  label: string;
  code: string;
  count: number;
  countHint: string;
  hasChildren: boolean;
  color?: string | null;
  inactive?: boolean;
}

export const CatalogTree: React.FC<Props> = ({ programs, index, selected, onSelect }) => {
  const [query, setQuery] = React.useState("");
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());
  const listRef = React.useRef<HTMLDivElement>(null);

  // Le chemin du nœud sélectionné est toujours déplié.
  React.useEffect(() => {
    const path = nodePath(index, selected);
    if (path.length === 0) return;
    setExpanded((prev) => {
      const ids = selected?.kind === "grade" ? path.slice(0, -1) : path;
      if (ids.every((n) => prev.has(n.id))) return prev;
      const next = new Set(prev);
      ids.forEach((n) => next.add(n.id));
      return next;
    });
  }, [index, selected]);

  const filter = React.useMemo(() => filterTree(programs, query), [programs, query]);
  const isOpen = React.useCallback(
    (id: string) => expanded.has(id) || filter.expand.has(id),
    [expanded, filter.expand],
  );

  const rows = React.useMemo(() => {
    const out: Row[] = [];
    const visible = (id: string) => !filter.visible || filter.visible.has(id);
    for (const p of programs) {
      if (!visible(p.program.id)) continue;
      const c = programCounts(p);
      out.push({
        node: { kind: "program", id: p.program.id },
        depth: 0,
        label: p.program.name,
        code: p.program.code,
        count: c.grades,
        countHint: `${plural(c.grades, "niveau", "niveaux")}, ${plural(c.subjects, "matière")}`,
        hasChildren: (p.stages ?? []).length > 0,
        color: p.program.color_primary,
        inactive: !p.program.is_active,
      });
      if (!isOpen(p.program.id)) continue;
      for (const s of p.stages ?? []) {
        if (!visible(s.stage.id)) continue;
        const n = (s.grades ?? []).length;
        out.push({
          node: { kind: "stage", id: s.stage.id },
          depth: 1,
          label: s.stage.name,
          code: s.stage.code,
          count: n,
          countHint: plural(n, "niveau", "niveaux"),
          hasChildren: n > 0,
        });
        if (!isOpen(s.stage.id)) continue;
        for (const g of s.grades ?? []) {
          if (!visible(g.grade.id)) continue;
          const m = (g.offerings ?? []).length;
          out.push({
            node: { kind: "grade", id: g.grade.id },
            depth: 2,
            label: g.grade.name,
            code: g.grade.code,
            count: m,
            countHint: plural(m, "matière offerte", "matières offertes"),
            hasChildren: false,
          });
        }
      }
    }
    return out;
  }, [programs, filter.visible, isOpen]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const choose = (r: Row) => {
    onSelect(r.node);
    if (r.hasChildren && !expanded.has(r.node.id)) toggle(r.node.id);
  };

  const focusRow = (i: number) => {
    const el = listRef.current?.querySelectorAll<HTMLElement>("[role=treeitem]")[i];
    el?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, i: number, r: Row) => {
    if (e.key === "ArrowDown") focusRow(Math.min(i + 1, rows.length - 1));
    else if (e.key === "ArrowUp") focusRow(Math.max(i - 1, 0));
    else if (e.key === "ArrowRight" && r.hasChildren && !isOpen(r.node.id)) toggle(r.node.id);
    else if (e.key === "ArrowLeft" && r.hasChildren && isOpen(r.node.id)) toggle(r.node.id);
    else if (e.key === "Enter" || e.key === " ") onSelect(r.node);
    else return;
    e.preventDefault();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Programme, étape, niveau…"
          slotProps={{
            htmlInput: { "aria-label": "Chercher dans le catalogue" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" aria-label="Effacer la recherche" onClick={() => setQuery("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
      <Box ref={listRef} role="tree" aria-label="Catalogue" sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1.5 }}>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
            Rien ne correspond à « {query} ».
          </Typography>
        )}
        {rows.map((r, i) => {
          const active = sameNode(selected, r.node);
          const open = isOpen(r.node.id);
          return (
            <Box
              key={`${r.node.kind}:${r.node.id}`}
              role="treeitem"
              aria-level={r.depth + 1}
              aria-selected={active}
              aria-expanded={r.hasChildren ? open : undefined}
              tabIndex={active || (!selected && i === 0) ? 0 : -1}
              onClick={() => choose(r)}
              onKeyDown={(e) => onKeyDown(e, i, r)}
              sx={(t) => ({
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                pl: 0.5 + r.depth * 2,
                pr: 1,
                minHeight: r.depth === 0 ? 36 : 32,
                mt: r.depth === 0 && i > 0 ? 0.75 : 0,
                borderRadius: 1.5,
                cursor: "pointer",
                userSelect: "none",
                color: r.inactive ? "text.secondary" : "text.primary",
                bgcolor: active ? alpha(t.palette.primary.main, 0.09) : "transparent",
                "&:hover": { bgcolor: active ? alpha(t.palette.primary.main, 0.12) : "action.hover" },
                "&:focus-visible": { outline: `2px solid ${alpha(t.palette.primary.main, 0.5)}`, outlineOffset: -2 },
              })}
            >
              <Box
                component="span"
                onClick={(e) => {
                  if (!r.hasChildren) return;
                  e.stopPropagation();
                  toggle(r.node.id);
                }}
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 1,
                  color: "text.secondary",
                  visibility: r.hasChildren ? "visible" : "hidden",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ChevronRightIcon
                  sx={{ fontSize: 18, transition: "transform 120ms", transform: open ? "rotate(90deg)" : "none" }}
                />
              </Box>
              {r.depth === 0 && <ColorDot color={r.color} size={9} />}
              {r.depth === 2 && (
                <Typography
                  component="span"
                  className="mono"
                  sx={{ color: active ? "primary.dark" : "text.secondary", minWidth: 34, flexShrink: 0, fontWeight: 600 }}
                >
                  {r.code}
                </Typography>
              )}
              <Typography
                component="span"
                noWrap
                dir="auto"
                title={r.label}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: r.depth === 0 ? "0.875rem" : "0.8125rem",
                  fontWeight: r.depth === 0 || active ? 600 : r.depth === 1 ? 550 : 400,
                  color: active ? "primary.dark" : "inherit",
                }}
              >
                {r.label}
                {r.inactive && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                    inactif
                  </Typography>
                )}
              </Typography>
              <Tooltip title={r.countHint} placement="right">
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.disabled", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
                >
                  {r.count}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CatalogTree;
